import type { ExperienceEventInput } from "../types/experience";
import type { PublicStats } from "../types/profile";
import { isRecord } from "../types/data";
import { useEffect, useRef } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  getDocsFromServer,
  getFirestore,
  query,
  where
} from "../utility/firestoreGateway";
import { useFirebase } from "../context/FirebaseContext";
import { useUserRatings } from "../context/UserRatingsContext";
import {
  applyPendingExperienceMutation,
  EXPERIENCE_MUTATION_PENDING,
  normalizeExperienceMutation
} from "../utility/experienceMutationOutbox";
import { dispatchExperienceEvents } from "../utility/experienceNotification";
import {
  getCurrentWeekId,
  getWeeklyQuestProgressRatings
} from "../utility/weeklyQuestCards";
import { revokeHistoricalRatingProgress } from "../utility/ratingProgressRevocation";
import { isDemoUserId } from "../utility/demoSession";
import { invalidateCachedValue } from "../utility/localDataCache";
import { getUserProfileCacheKey } from "../utility/profileCache";
import { getWeeklyStreakStats } from "../utility/weeklyStreakTracker";

interface RecoveryResult { applied: boolean; reason?: string; experienceEvents?: ExperienceEventInput[]; event?: ExperienceEventInput; publicStats?: PublicStats; }

// Pending XP mutations live in rating history, not in IndexedDB. This small
// recovery worker always queries the server and therefore survives a cleared
// browser cache, a closed tab and a failed immediate profile transaction.
function ExperienceMutationRecovery({ userId }: { userId?: string | null }) {
  const { auth, firestore } = useFirebase();
  const [authUser] = useAuthState(auth);
  const {
    userId: ratingsOwnerUserId,
    ratings = [],
    ratingHistory = [],
    ratingsSyncRevision,
    ratingsSyncReady,
    loading,
    historyLoading,
    initialSyncLoading
  } = useUserRatings();
  const inFlightRef = useRef(false);
  const rerunRequestedRef = useRef(false);
  const retryAfterProfileUpdateRef = useRef(false);

  const ready = Boolean(
    userId &&
    authUser?.uid === userId &&
    ratingsOwnerUserId === userId &&
    !isDemoUserId(userId) &&
    !loading &&
    !historyLoading &&
    !initialSyncLoading &&
    ratingsSyncReady === true
  );

  useEffect(() => {
    if (!ready || !userId) return undefined;
    let cancelled = false;
    const db = getFirestore();

    const recover = async () => {
      if (cancelled) return;
      if (inFlightRef.current) {
        rerunRequestedRef.current = true;
        return;
      }
      inFlightRef.current = true;
      try {
        do {
          rerunRequestedRef.current = false;
          let needsProfileRetry = false;
          const pendingQuery = query(
            collection(db, "userRatings", userId, "history"),
            where("experienceMutation.status", "==", EXPERIENCE_MUTATION_PENDING)
          );
          const snapshot = await getDocsFromServer(pendingQuery);
          const pendingDocuments = [...(snapshot.docs || [])]
            .sort((left, right) => String(left.id).localeCompare(String(right.id)));
          const progressRatings = getWeeklyQuestProgressRatings(
            ratings,
            ratingHistory
          );

          let settlementTarget: { filmId: string; mutationId: string } | null = null;
          let prerequisiteFailed = false;

          for (const documentSnapshot of pendingDocuments) {
            if (cancelled) return;
            const history = documentSnapshot.data() || {};
            const mutation = normalizeExperienceMutation(
              history.experienceMutation
            );
            if (!mutation || mutation.status !== EXPERIENCE_MUTATION_PENDING) {
              continue;
            }

            if (history.active === false) {
              try {
                const revokedProgress = await revokeHistoricalRatingProgress({
                  firestore,
                  userId,
                  filmId: documentSnapshot.id,
                  currentWeekId: getCurrentWeekId(),
                  progressRatings,
                  expectedRatingsRevision: Math.max(
                    0,
                    Number(ratingsSyncRevision) || 0
                  )
                });
                if (revokedProgress && typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent(
                    "rating-game-progress-revoked",
                    { detail: { userId, ...revokedProgress } }
                  ));
                }
                const qualityWeekKey = String(
                  history.weekQualityWeekKey || ""
                ).trim();
                if (qualityWeekKey) {
                  await getWeeklyStreakStats(
                    userId,
                    ratings,
                    firestore,
                    true,
                    {
                      cache: false,
                      recalculateQualityWeeks: [qualityWeekKey],
                      recalculateStreakWeeks: [qualityWeekKey]
                    }
                  );
                }
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent(
                    "experience-source-reconciled",
                    { detail: { userId, filmId: documentSnapshot.id } }
                  ));
                }
              } catch (error) {
                // XP and progress must settle in the same logical order as the
                // original delete. Keep the outbox pending and retry later.
                console.warn(
                  "Не удалось восстановить отзыв игрового прогресса:",
                  error
                );
                prerequisiteFailed = true;
                continue;
              }
            }

            settlementTarget ||= {
              filmId: documentSnapshot.id,
              mutationId: mutation.id
            };
          }

          // One trusted settlement applies every pending history marker that
          // was returned by the query. Calling it once per document used to
          // turn a large import/recovery batch into hundreds of sequential
          // full-cache checks (and, before the server cache warmed, full
          // ratings scans). Keep the whole snapshot as one recovery unit.
          if (settlementTarget && !prerequisiteFailed) {
            const result: RecoveryResult = await applyPendingExperienceMutation({
              db,
              userId,
              filmId: settlementTarget.filmId,
              expectedMutationId: settlementTarget.mutationId
            });
            if (result?.reason === "missing-experience-baseline") {
              needsProfileRetry = true;
            }
            dispatchExperienceEvents(
              result?.experienceEvents ||
              (result?.event ? [result.event] : []),
              userId
            );
            if (result?.publicStats && typeof window !== "undefined") {
              void invalidateCachedValue(getUserProfileCacheKey(userId));
              window.dispatchEvent(new CustomEvent(
                "movie-profile-experience-updated",
                {
                  detail: {
                    userId,
                    publicStats: result.publicStats,
                    source: "experience-mutation-recovery"
                  }
                }
              ));
            }
          }
          retryAfterProfileUpdateRef.current = needsProfileRetry;
        } while (!cancelled && rerunRequestedRef.current);
      } catch (error) {
        // Offline/server errors leave every marker untouched. The online event,
        // a new marker signal or the next authenticated mount retries them.
        console.warn("Не удалось восстановить ожидающие XP-операции:", error);
      } finally {
        inFlightRef.current = false;
      }
    };

    const handleRetry = (event: Event) => {
      const detail: unknown = event instanceof CustomEvent ? event.detail : null;
      const eventData = isRecord(detail) ? detail : {};
      const eventUserId = eventData.userId;
      if (eventUserId && eventUserId !== userId) return;
      if (eventData.source === "experience-mutation-recovery") return;
      if (
        event?.type === "movie-profile-experience-updated" &&
        !retryAfterProfileUpdateRef.current &&
        !inFlightRef.current
      ) return;
      void recover();
    };

    void recover();
    if (typeof window !== "undefined") {
      window.addEventListener("online", handleRetry);
      window.addEventListener("experience-mutation-pending", handleRetry);
      // A profile refresh or another trusted settlement may have initialized
      // a legacy numeric baseline. Retry the still-pending signed mutation
      // only after that authoritative snapshot is available.
      window.addEventListener("movie-profile-experience-updated", handleRetry);
    }
    return () => {
      cancelled = true;
      if (typeof window !== "undefined") {
        window.removeEventListener("online", handleRetry);
        window.removeEventListener("experience-mutation-pending", handleRetry);
        window.removeEventListener("movie-profile-experience-updated", handleRetry);
      }
    };
  }, [
    firestore,
    ratingHistory,
    ratings,
    ratingsSyncRevision,
    ready,
    userId
  ]);

  return null;
}

export default ExperienceMutationRecovery;
